import {AnonymousAuth} from './services';
import {ApplicationInsights} from '@microsoft/applicationinsights-web';
import {
  Api,
  Config,
  ConsoleLogger,
  Globalization,
  JSONStorage,
  MessageHub,
  Router,
  StorageCache,
  Telemetry,
  RealmCache,
  TranslationsApi,
} from '@planday-corp/core/services';
import Notifications from '@planday-corp/core/services/Notifications';
import {QueryClient} from '@tanstack/react-query';

export type EnforcedLanguageSetting =
  Globalization['settings']['enforcedLanguage'];

interface AnonymousContextProps {
  /*
   * Pre-enable logger for debugging
   */
  isDebug?: boolean;
  /**
   * Translation categories
   */
  categories?: string[];
  /*
   * Router-type of the React Router
   */
  routerType?: 'Hash' | 'Browser';

  /*
   * Enforced language for the app, by default it will use the browser's language
   * requirement is to read the language from the url subpath
   */
  enforcedLanguage?: EnforcedLanguageSetting;
}

function initiateTelemetry(
  appInsights: ApplicationInsights,
  sessionJSONStorage: JSONStorage
) {
  // Our telemetry service only works if the appInsights object is attached to the window object
  // This is a workaround to make it work

  // @ts-ignore
  window.appInsights = appInsights;
  // @ts-ignore
  window.appInsights.version = appInsights.snippet.version;

  return new Telemetry({origin}, {storage: sessionJSONStorage});
}

export const config = new Config({
  sources: [window.DevConfig, window.PlandayConfig],
});

export function createAzureAppInsightsContext(
  config: Config,
  props: AnonymousContextProps
) {
  const clientId = config.get<string>('clientId');
  const {
    isDebug = false,
    routerType = 'Hash',
    categories = [],
    enforcedLanguage,
  } = props;
  const localJSONStorage = new JSONStorage({storageType: 'local'});
  const sessionJSONStorage = new JSONStorage({storageType: 'session'});
  const logger = new ConsoleLogger(
    {isEnabled: isDebug ? true : undefined},
    {storage: sessionJSONStorage}
  );

  const appInsights = new ApplicationInsights({
    config: {
      enableCorsCorrelation: true,
      instrumentationKey: config.get<string>(
        'applicationInsights.instrumentationKey'
      ),
    },
  });

  appInsights.loadAppInsights();
  appInsights.trackPageView({name: 'PortalSignup.PageLoaded'});

  const router = new Router({type: routerType});
  const cache = new StorageCache({name: 'test'}, {storage: localJSONStorage});
  const transientCache = new RealmCache({name: 'transient_cache'});

  const telemetry = initiateTelemetry(appInsights, sessionJSONStorage);

  const messageHub = new MessageHub({}, {logger});
  const auth = new AnonymousAuth();

  const translationsApi = new TranslationsApi(
    {
      serverUrl: config.get('internalServices.translationsApiUrl'),
      clientId: config.get('clientId'),
      version: 'v1.0',
      subpath: 'translations',
    },
    {auth}
  );

  const globalization = new Globalization(
    {
      categories,
      clientId,
      isAnonymous: true,
      enforcedLanguage,
    },
    {auth, cache, transientCache, translationsApi}
  );

  const _logger = logger.create('Common-Context');
  _logger.log('Auth type:', Object.prototype.toString.call(auth));

  const notifications = new Notifications();

  return {
    messageHub,
    globalization,
    logger,
    router,
    auth,
    cache,
    telemetry,
    localJSONStorage,
    sessionJSONStorage,
    notifications,
    translationsApi,
  };
}

export function createAppContext(enforcedLanguage?: EnforcedLanguageSetting) {
  const context = createAzureAppInsightsContext(config, {
    routerType: 'Browser',
    categories: [
      'CommonTerms',
      'Onboarding',
      'CommonFormatting',
      'SignUp',
      'Countries',
    ],
    isDebug: process.env.NODE_ENV === 'development',
    enforcedLanguage,
  });

  const queryClient = new QueryClient();

  const signupApi = new Api(
    {
      serverUrl: config.get('internalServices.signupApiUrl'),
      clientId: config.get('clientId'),
    },
    {auth: context.auth}
  );

  return {
    ...context,
    queryClient,
    config,
    signupApi,
  };
}
